import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AdsController } from "@/components/AdsController";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import CanonicalUrl from "@/components/CanonicalUrl";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import DistrictPromptDialog from "./components/DistrictPromptDialog";
import { AppLayout } from "./components/AppLayout";
import { OfflineSync } from "./components/OfflineSync";

// Retry dynamic imports to handle stale chunk errors after deploys
function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err: any) {
      const msg = String(err?.message || "");
      if (
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module")
      ) {
        const key = "lovable_chunk_reload_at";
        const last = Number(sessionStorage.getItem(key) || 0);
        if (Date.now() - last > 10000) {
          sessionStorage.setItem(key, String(Date.now()));
          window.location.reload();
          return new Promise<never>(() => {});
        }
      }
      throw err;
    }
  });
}

const Index = lazyWithRetry(() => import("./pages/Index"));
const ExamPage = lazyWithRetry(() => import("./pages/ExamPage"));
const ResultsPage = lazyWithRetry(() => import("./pages/ResultsPage"));
const ResultsHistoryPage = lazyWithRetry(() => import("./pages/ResultsHistoryPage"));
const AdminPage = lazyWithRetry(() => import("./pages/AdminPage"));
const AdminLoginPage = lazyWithRetry(() => import("./pages/AdminLoginPage"));
const AuthPage = lazyWithRetry(() => import("./pages/AuthPage"));
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"));
const YouTubePage = lazyWithRetry(() => import("./pages/YouTubePage"));
const TermsOfServicePage = lazyWithRetry(() => import("./pages/TermsOfServicePage"));
const NotesPage = lazyWithRetry(() => import("./pages/NotesPage"));
const BatchPage = lazyWithRetry(() => import("./pages/BatchPage"));
const SubjectTestsPage = lazyWithRetry(() => import("./pages/SubjectTestsPage"));
const PublicQuizPage = lazyWithRetry(() => import("./pages/PublicQuizPage"));
const AddPublicQuizPage = lazyWithRetry(() => import("./pages/PublicQuizPage").then(module => ({ default: module.AddPublicQuizPage })));
const AICoachPage = lazyWithRetry(() => import("./pages/AICoachPage"));
const MistakesPage = lazyWithRetry(() => import("./pages/MistakesPage"));
const BooksPage = lazyWithRetry(() => import("./pages/BooksPage"));
const BookDetailsPage = lazyWithRetry(() => import("./pages/BookDetailsPage"));
const CartPage = lazyWithRetry(() => import("./pages/CartPage"));
const CheckoutPage = lazyWithRetry(() => import("./pages/CheckoutPage"));
const MyOrdersPage = lazyWithRetry(() => import("./pages/MyOrdersPage"));
const CurrentAffairsPage = lazyWithRetry(() => import("./pages/CurrentAffairsPage"));
const DownloadsPage = lazyWithRetry(() => import("./pages/DownloadsPage"));
const RewardsPage = lazyWithRetry(() => import("./pages/RewardsPage"));
const JobAlertsPage = lazyWithRetry(() => import("./pages/JobAlertsPage"));
const JobDetailPage = lazyWithRetry(() => import("./pages/JobDetailPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const NotificationSettingsPage = lazyWithRetry(() => import("./pages/NotificationSettingsPage"));
const ExamHubPage = lazyWithRetry(() => import("./pages/ExamHubPage"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

// Warm up the most-used page files in the background after first load,
// so moving between pages opens instantly instead of downloading each time.
if (typeof window !== "undefined") {
  const warm = () => {
    [
      () => import("./pages/Index"),
      () => import("./pages/JobAlertsPage"),
      () => import("./pages/PublicQuizPage"),
      () => import("./pages/AICoachPage"),
      () => import("./pages/MistakesPage"),
      () => import("./pages/SubjectTestsPage"),
      () => import("./pages/ExamHubPage"),
      () => import("./pages/ProfilePage"),
      () => import("./pages/ResultsHistoryPage"),
      () => import("./pages/RewardsPage"),
      () => import("./pages/JobDetailPage"),
      () => import("./pages/BatchPage"),
      () => import("./pages/ExamPage"),
      () => import("./pages/ResultsPage"),
    ].forEach((f, i) => setTimeout(() => f().catch(() => {}), i * 150));
  };
  const ric = (window as any).requestIdleCallback as ((cb: () => void) => void) | undefined;
  window.addEventListener("load", () => (ric ? ric(warm) : setTimeout(warm, 1500)), { once: true });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Show cached data instantly, then refresh in the background so the
      // UI never blocks on a network round-trip.
      staleTime: 2 * 60_000,
      gcTime: 10 * 60_000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
      networkMode: "offlineFirst",
    },
  },
});

// Saves loaded data on the device so pages still show it without internet.
try { window.localStorage.removeItem("isc-query-cache"); } catch { /* ignore */ }

/** Minimal skeleton shell — no spinners, no text, no layout shift. */
const PageLoader = () => (
  <div className="min-h-screen bg-background p-4 space-y-3">
    <div className="h-10 w-full rounded-lg bg-muted/60 animate-pulse" />
    <div className="h-24 w-full rounded-xl bg-muted/40 animate-pulse" />
    <div className="grid grid-cols-2 gap-3">
      <div className="h-20 rounded-xl bg-muted/40 animate-pulse" />
      <div className="h-20 rounded-xl bg-muted/40 animate-pulse" />
    </div>
  </div>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return <AppLayout>{children}</AppLayout>;
}

// Same auth check as ProtectedRoute, but without the sidebar / bottom nav —
// used by the exam page, which has its own full-screen layout.
function BareProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <DistrictPromptDialog />
        <Toaster />
        <Sonner />
        <OfflineSync />
        <BrowserRouter>
          <CanonicalUrl />
          <AdsController />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
              <Route path="/index" element={<Navigate to="/" replace />} />
              <Route path="/exam" element={<BareProtectedRoute><ExamPage /></BareProtectedRoute>} />
              <Route path="/results" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
              <Route path="/results-history" element={<ProtectedRoute><ResultsHistoryPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/youtube" element={<ProtectedRoute><YouTubePage /></ProtectedRoute>} />
              <Route path="/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
              <Route path="/batch" element={<ProtectedRoute><BatchPage /></ProtectedRoute>} />
              <Route path="/exams/:exam" element={<ProtectedRoute><ExamHubPage /></ProtectedRoute>} />
              <Route path="/subject/:slug" element={<ProtectedRoute><SubjectTestsPage /></ProtectedRoute>} />
              <Route path="/public-quiz/add" element={<ProtectedRoute><AddPublicQuizPage /></ProtectedRoute>} />
              <Route path="/public-quiz" element={<ProtectedRoute><PublicQuizPage /></ProtectedRoute>} />
              <Route path="/ai-coach" element={<ProtectedRoute><AICoachPage /></ProtectedRoute>} />
              <Route path="/mistakes" element={<ProtectedRoute><MistakesPage /></ProtectedRoute>} />
              <Route path="/books" element={<ProtectedRoute><BooksPage /></ProtectedRoute>} />
              <Route path="/books/:id" element={<ProtectedRoute><BookDetailsPage /></ProtectedRoute>} />
              <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
              <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
              <Route path="/my-orders" element={<ProtectedRoute><MyOrdersPage /></ProtectedRoute>} />
              <Route path="/jobs" element={<ProtectedRoute><JobAlertsPage /></ProtectedRoute>} />
              <Route path="/jobs/:id" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
              <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
              <Route path="/downloads" element={<ProtectedRoute><DownloadsPage /></ProtectedRoute>} />
              <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/current-affairs" element={<CurrentAffairsPage />} />
              <Route path="/admin-login" element={<AdminLoginPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
